import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Loader2, Plus, Trash2, Users, X } from 'lucide-react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import FilterSelect from '../../components/common/FilterSelect';
import ExportButton from '../../components/common/ExportButton';
import Modal from '../../components/common/Modal';
import { downloadExcelWorkbook } from '../../utils/exportExcel';
import {
  DEFAULT_SHIFT_OPTIONS,
  DEFAULT_TASK_OPTIONS,
  ROSTER_DAYS,
  ROSTER_SLOT_MINUTES,
  buildTaskSlotKey,
  calculateWeeklyMinutes,
  createStaffMember,
  formatHourValue,
  getShiftSlotState,
  normalizeShiftInput,
  parseRosterTime,
  parseShift,
} from '../../utils/workforceRoster';
import { useAuthStore } from '../../store/authStore';
import { workforceRosterAPI } from '../../services/api';
import { useStoreContextStore } from '../../store/storeContextStore';

const OVERTIME_THRESHOLD_MINUTES = 40 * 60;
const HOUR_SLOTS = ROSTER_SLOT_MINUTES.filter((minutes) => minutes % 60 === 0);
const BREAK_TASK_VALUE = '__BREAK__';
const FULL_TIME_THRESHOLD_MINUTES = 8 * 60;

const getEmptyState = () => ({
  staff: [],
  roster: ROSTER_DAYS.reduce((acc, day) => ({ ...acc, [day]: {} }), {}),
  tasks: ROSTER_DAYS.reduce((acc, day) => ({ ...acc, [day]: {} }), {}),
  taskOptions: [...DEFAULT_TASK_OPTIONS],
  shiftOptions: [...DEFAULT_SHIFT_OPTIONS],
});

const getInitialState = () => getEmptyState();

const stateClassBySlot = {
  work: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  break: 'bg-amber-50 text-amber-700 border-amber-200',
  'day-off': 'bg-rose-50 text-rose-700 border-rose-200',
  leave: 'bg-orange-50 text-orange-700 border-orange-200',
  holiday: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  inactive: 'bg-slate-50 text-slate-400 border-slate-200',
};

const getManualBreakMinutesForMemberDay = (tasks = {}, memberId, day) => {
  const values = Object.values(tasks?.[day]?.[memberId] || {});
  return values.filter((value) => toTaskCell(value).label === BREAK_TASK_VALUE).length * 60;
};

const formatRosterUserLabel = (entry) => {
  const name = String(entry?.name || '').trim();
  const role = String(entry?.role || '').trim();
  const storeName = String(entry?.storeName || entry?.stores?.name || '').trim();
  const labelParts = [role, storeName].filter(Boolean);
  if (!name) return labelParts.join(', ') || 'Unknown User';
  return labelParts.length ? `${name} (${labelParts.join(', ')})` : name;
};

const toTaskCell = (value) => {
  if (value && typeof value === 'object') {
    return {
      label: String(value.label || value.task || '').trim(),
      assignedByRole: String(value.assignedByRole || '').trim(),
      assignedByName: String(value.assignedByName || '').trim(),
    };
  }
  return {
    label: String(value || '').trim(),
    assignedByRole: '',
    assignedByName: '',
  };
};

const WorkforceRoster = () => {
  const { user, hasFeatureAccess, isAdmin, isManager } = useAuthStore();
  const canViewRoster = hasFeatureAccess('workforce_roster.view');
  const canEditRoster = hasFeatureAccess('workforce_roster.manage');
  const normalizedRole = String(user?.role || '').trim().toLowerCase();
  const currentUserId = String(user?.id || '').trim();
  const isAssistantManagerUser = normalizedRole === 'assistant manager';
  const isManagerUser = isManager();
  const isSupervisorUser = normalizedRole === 'supervisor';
  const isStaffUser = normalizedRole === 'staff';
  const canManageRoster = canEditRoster && (isAdmin() || isManagerUser || isSupervisorUser || isAssistantManagerUser);
  const [rosterState, setRosterState] = useState(getInitialState);
  const { selectedStore } = useStoreContextStore();
  const [activeTab, setActiveTab] = useState('roster');
  const [selectedDay, setSelectedDay] = useState(ROSTER_DAYS[0]);
  const [selectedUserIdToAdd, setSelectedUserIdToAdd] = useState('');
  const [newShiftOption, setNewShiftOption] = useState('');
  const [newTaskOption, setNewTaskOption] = useState('');
  const [copyTaskModal, setCopyTaskModal] = useState({
    isOpen: false,
    staffId: '',
    staffName: '',
    sourceDay: ROSTER_DAYS[0],
    targetDays: [],
    overwrite: true,
  });
  const [syncState, setSyncState] = useState('idle');
  const [hasHydratedFromServer, setHasHydratedFromServer] = useState(false);
  const saveTimeoutRef = useRef(null);
  const pendingSaveToastMessageRef = useRef('');

  const hasToken = !!localStorage.getItem('token');
  const rosterScopeStoreId = selectedStore?.storeInfoId
    || selectedStore?.store_info_id
    || '';
  const rosterScopeKey = rosterScopeStoreId ? `store:${rosterScopeStoreId}` : 'none';
  const canLoadRosterScope = Boolean(rosterScopeStoreId);

  const { data: usersRes, isLoading: loadingUsers, isError: usersLoadError } = useQuery(
    ['rosterUsers', user?.id, rosterScopeKey, normalizedRole],
    () => workforceRosterAPI.getUsers({ storeInfoId: rosterScopeStoreId }),
    {
      enabled: hasToken && canViewRoster && canManageRoster && canLoadRosterScope,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const { data: rosterRes, isLoading: loadingRoster, isError: rosterLoadError } = useQuery(
    ['workforceRoster', rosterScopeKey, user?.id],
    () => workforceRosterAPI.get({ storeInfoId: rosterScopeStoreId }),
    {
      enabled: hasToken && canViewRoster && canLoadRosterScope,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    setRosterState(getEmptyState());
    setHasHydratedFromServer(false);
  }, [user?.id, rosterScopeKey]);

  useEffect(() => {
    if (!canViewRoster || !canLoadRosterScope) return;
    const payload = rosterRes?.data?.data;
    if (payload) {
      const nextState = {
        ...getEmptyState(),
        ...payload,
      };
      setRosterState(nextState);
      setSyncState('saved');
      setHasHydratedFromServer(true);
      return;
    }
    if (rosterLoadError) {
      setHasHydratedFromServer(true);
    }
  }, [canLoadRosterScope, canViewRoster, rosterLoadError, rosterRes]);

  const { staff, roster, tasks, taskOptions, shiftOptions } = rosterState;
  const isRosterHydrating = canViewRoster && canLoadRosterScope && !hasHydratedFromServer && !rosterLoadError;
  const normalizedShiftOptions = useMemo(() => {
    const optionSet = new Set();
    (shiftOptions || []).forEach((option) => {
      const normalized = normalizeShiftInput(option);
      if (normalized) optionSet.add(normalized);
    });
    if (optionSet.size === 0) {
      DEFAULT_SHIFT_OPTIONS.forEach((option) => optionSet.add(option));
    }
    return Array.from(optionSet);
  }, [shiftOptions]);
  const shiftSelectOptions = useMemo(() => ([
    { value: '', label: 'Select shift' },
    ...normalizedShiftOptions.map((option) => ({ value: option, label: option })),
  ]), [normalizedShiftOptions]);
  const taskSelectOptions = useMemo(() => ([
    { value: '', label: 'Task' },
    { value: BREAK_TASK_VALUE, label: 'Break' },
    ...((taskOptions.length ? taskOptions : DEFAULT_TASK_OPTIONS).map((option) => ({ value: option, label: option }))),
  ]), [taskOptions]);
  const assignableUsers = useMemo(() => {
    const nextUsers = new Map();
    const selectedScopeStoreId = String(rosterScopeStoreId || '');

    const addUser = (entry, { filterRoles = false } = {}) => {
      const id = String(entry?.id || '').trim();
      const name = String(entry?.name || '').trim();
      if (!id || !name || id === currentUserId) return;

      const role = String(entry?.role || entry?.roles?.name || '').trim();
      const normalizedRole = role.toLowerCase();
      if (filterRoles && role && !['staff', 'supervisor', 'assistant manager', 'manager', 'admin'].includes(normalizedRole)) {
        return;
      }

      const storeId = String(entry?.storeInfoId || entry?.store_info_id || entry?.storeId || entry?.store_id || '').trim();
      const storeName = String(entry?.storeName || entry?.stores?.name || '').trim();
      if (selectedScopeStoreId && storeId && storeId !== selectedScopeStoreId) {
        return;
      }
      const current = nextUsers.get(id);
      nextUsers.set(id, current
        ? {
            ...current,
            name: current.name || name,
            role: current.role || role,
            storeInfoId: current.storeInfoId || storeId,
            store_info_id: current.store_info_id || storeId,
            storeId: current.storeId || storeId,
            storeName: current.storeName || storeName,
          }
        : { id, name, role, storeInfoId: storeId, store_info_id: storeId, storeId, storeName });
    };

    (usersRes?.data?.data || []).forEach((entry) => addUser(entry, { filterRoles: true }));
    (staff || []).forEach((entry) => addUser(entry));

    return Array.from(nextUsers.values()).sort((left, right) => (
      String(left.name || '').localeCompare(String(right.name || ''))
    ));
  }, [currentUserId, rosterScopeStoreId, staff, usersRes]);
  useEffect(() => {
    if (!canManageRoster || !hasHydratedFromServer || !canLoadRosterScope) return undefined;
    if (loadingUsers || usersLoadError) return undefined;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSyncState('saving');
    saveTimeoutRef.current = setTimeout(async () => {
      const saveToastMessage = pendingSaveToastMessageRef.current;
      if (saveToastMessage) {
        toast.loading(saveToastMessage, { id: 'workforce-roster-save' });
      }

      try {
        await workforceRosterAPI.sync({
          ...(rosterScopeStoreId ? { storeInfoId: rosterScopeStoreId } : { scope: 'all' }),
          ...rosterState,
        });
        setSyncState('saved');
        if (saveToastMessage) {
          toast.success('Roster saved.', { id: 'workforce-roster-save' });
          pendingSaveToastMessageRef.current = '';
        }
      } catch (error) {
        setSyncState('error');
        if (saveToastMessage) {
          toast.error('Could not save roster. Please try again.', { id: 'workforce-roster-save' });
          pendingSaveToastMessageRef.current = '';
        }
      }
    }, 700);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    canManageRoster,
    canLoadRosterScope,
    hasHydratedFromServer,
    loadingUsers,
    usersLoadError,
    rosterState,
    rosterScopeStoreId,
  ]);
  const rosterMemberIdSet = useMemo(() => (
    new Set(staff.map((entry) => String(entry.id)).filter(Boolean))
  ), [staff]);
  const hasSelectableAssignableUsers = useMemo(
    () => assignableUsers.some((entry) => !rosterMemberIdSet.has(String(entry.id))),
    [assignableUsers, rosterMemberIdSet]
  );
  const addStaffUserOptions = useMemo(() => {
    const placeholder = loadingUsers
      ? 'Loading users...'
      : usersLoadError
        ? 'Failed to load users'
        : assignableUsers.length === 0
          ? 'No users available'
          : !hasSelectableAssignableUsers
            ? 'All assignable users are already on the roster'
          : 'Select user';
    return [
      { value: '', label: placeholder },
      ...assignableUsers.map((entry) => ({
        value: String(entry.id),
        label: formatRosterUserLabel(entry),
        disabled: rosterMemberIdSet.has(String(entry.id)),
      })),
    ];
  }, [assignableUsers, hasSelectableAssignableUsers, loadingUsers, rosterMemberIdSet, usersLoadError]);
  const copySourceTaskCount = useMemo(() => {
    if (!copyTaskModal.staffId) return 0;
    return Object.values(tasks?.[copyTaskModal.sourceDay]?.[copyTaskModal.staffId] || {})
      .filter((value) => toTaskCell(value).label)
      .length;
  }, [copyTaskModal.sourceDay, copyTaskModal.staffId, tasks]);
  const copyTargetDayOptions = useMemo(() => {
    if (!copyTaskModal.staffId) return [];
    return ROSTER_DAYS
      .filter((day) => day !== copyTaskModal.sourceDay)
      .map((day) => {
        const shift = roster?.[day]?.[copyTaskModal.staffId] || '';
        const isWorkingDay = Boolean(parseShift(shift));
        return {
          day,
          shift,
          isWorkingDay,
          reason: isWorkingDay ? '' : (shift || 'No shift'),
        };
      });
  }, [copyTaskModal.sourceDay, copyTaskModal.staffId, roster]);

  const visibleStaff = useMemo(() => {
    const scopedStaff = rosterScopeStoreId
      ? staff.filter((member) => String(member?.storeInfoId || member?.store_info_id || member?.storeId || member?.store_id || '') === String(rosterScopeStoreId))
      : staff;

    if (isStaffUser) {
      return scopedStaff.filter((member) => String(member?.id || '') === currentUserId);
    }
    return scopedStaff.filter((member) => String(member?.id || '') !== currentUserId);
  }, [currentUserId, isStaffUser, rosterScopeStoreId, staff]);

  const buildWeeklyRosterRows = () => ([
    ['Staff', ...ROSTER_DAYS, 'Weekly Paid Hours'],
    ...visibleStaff.map((member) => {
      const weeklyMinutes = calculateWeeklyMinutes(roster, member.id);
      const breakMinutes = ROSTER_DAYS.reduce((sum, day) => (
        sum + getManualBreakMinutesForMemberDay(tasks, member.id, day)
      ), 0);
      const paidMinutes = Math.max(0, weeklyMinutes - breakMinutes);
      return [
        member.name,
        ...ROSTER_DAYS.map((day) => roster?.[day]?.[member.id] || ''),
        formatHourValue(paidMinutes),
      ];
    }),
  ]);

  const buildTaskAssignmentRows = () => {
    if (isStaffUser && visibleStaff.length > 0) {
      const member = visibleStaff[0];
      return [
        ['Weekday', ...HOUR_SLOTS.map((minutes) => buildTaskSlotKey(minutes))],
        ...ROSTER_DAYS.map((day) => [
          day,
          ...HOUR_SLOTS.map((minutes) => {
            const slotKey = buildTaskSlotKey(minutes);
            const cell = toTaskCell(tasks?.[day]?.[member.id]?.[slotKey]);
            if (!cell.label) return '';
            return cell.assignedByRole ? `${cell.label} (${cell.assignedByRole})` : cell.label;
          }),
        ]),
      ];
    }
    return [
      ['Staff', ...HOUR_SLOTS.map((minutes) => buildTaskSlotKey(minutes))],
      ...visibleStaff.map((member) => {
        const row = [member.name];
        HOUR_SLOTS.forEach((minutes) => {
          const slotKey = buildTaskSlotKey(minutes);
          const cell = toTaskCell(tasks?.[selectedDay]?.[member.id]?.[slotKey]);
          row.push(cell.label ? (cell.assignedByRole ? `${cell.label} (${cell.assignedByRole})` : cell.label) : '');
        });
        return row;
      }),
    ];
  };

  const exportAllRosterData = async () => {
    const weeklyRows = buildWeeklyRosterRows();
    const taskRows = isStaffUser
      ? buildTaskAssignmentRows()
      : [
          ['Staff', ...HOUR_SLOTS.map((minutes) => buildTaskSlotKey(minutes))],
          ...visibleStaff.map((member) => {
            const row = [member.name];
            HOUR_SLOTS.forEach((minutes) => {
              const slotKey = buildTaskSlotKey(minutes);
              const cell = toTaskCell(tasks?.[selectedDay]?.[member.id]?.[slotKey]);
              row.push(cell.label ? (cell.assignedByRole ? `${cell.label} (${cell.assignedByRole})` : cell.label) : '');
            });
            return row;
          }),
        ];

    const toSheet = (rows, defaultSheetName) => {
      const [headerRow, ...bodyRows] = rows;
      const columns = (headerRow || []).map((header, index) => ({
        header: String(header || `Column ${index + 1}`),
        key: `c${index}`,
        width: 20,
      }));
      const sheetRows = bodyRows.map((row) => (
        columns.reduce((acc, column, colIndex) => ({ ...acc, [column.key]: row?.[colIndex] ?? '' }), {})
      ));
      return { sheetName: defaultSheetName, columns, rows: sheetRows };
    };

    await downloadExcelWorkbook({
      fileName: `workforce-roster-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheets: [
        toSheet(weeklyRows, 'Weekly Roster'),
        toSheet(taskRows, isStaffUser ? 'Staff Task Assignment' : `Task Distribution ${selectedDay}`),
      ],
    });
  };

  const updateState = (updater, saveToastMessage = '') => {
    if (saveToastMessage) {
      pendingSaveToastMessageRef.current = saveToastMessage;
    }
    setRosterState((current) => updater(current));
  };

  const handleAddStaff = () => {
    if (!canManageRoster) return;
    if (!selectedUserIdToAdd) return;

    const selectedUser = assignableUsers.find((entry) => String(entry.id) === String(selectedUserIdToAdd));
    if (!selectedUser) return;
    if (rosterMemberIdSet.has(String(selectedUser.id))) {
      toast('This user is already on the roster.');
      return;
    }

    updateState((current) => {
      const member = createStaffMember(String(selectedUser.name), current.staff);
      member.id = String(selectedUser.id);
      member.storeInfoId = String(selectedUser.storeInfoId || selectedUser.store_info_id || selectedUser.storeId || selectedUser.store_id || rosterScopeStoreId || '');
      member.store_info_id = member.storeInfoId;
      member.storeId = member.storeInfoId;
      member.store_id = member.storeInfoId;
      member.role = String(selectedUser.role || '').trim();
      return {
        ...current,
        staff: [...current.staff, member],
        roster: ROSTER_DAYS.reduce((acc, day) => ({
          ...acc,
          [day]: {
            ...(current.roster[day] || {}),
            [member.id]: '',
          },
        }), {}),
        tasks: ROSTER_DAYS.reduce((acc, day) => ({
          ...acc,
          [day]: {
            ...(current.tasks[day] || {}),
            [member.id]: {},
          },
        }), {}),
      };
    }, 'Adding roster member...');
    setSelectedUserIdToAdd('');
  };

  const handleRemoveStaff = (staffId) => {
    if (!canManageRoster) return;
    updateState((current) => ({
      ...current,
      staff: current.staff.filter((member) => member.id !== staffId),
      roster: ROSTER_DAYS.reduce((acc, day) => {
        const nextDay = { ...(current.roster[day] || {}) };
        delete nextDay[staffId];
        return { ...acc, [day]: nextDay };
      }, {}),
      tasks: ROSTER_DAYS.reduce((acc, day) => {
        const nextDay = { ...(current.tasks[day] || {}) };
        delete nextDay[staffId];
        return { ...acc, [day]: nextDay };
      }, {}),
    }), 'Removing roster member...');
  };

  const handleRenameStaff = (staffId, nameValue) => {
    if (!canManageRoster) return;
    updateState((current) => ({
      ...current,
      staff: current.staff.map((member) => (
        member.id === staffId ? { ...member, name: nameValue } : member
      )),
    }));
  };

  const commitShiftValue = (staffId, day, shiftValue) => {
    if (!canManageRoster) return;
    const normalizedShift = normalizeShiftInput(shiftValue);
    if (shiftValue && !normalizedShift) {
      toast.error('Invalid shift format. Use HH:MM-HH:MM, Leave, Holiday, or Day Off.');
      return;
    }

    updateState((current) => {
      const existingTasks = current.tasks?.[day]?.[staffId] || {};
      const prunedTasks = Object.entries(existingTasks).reduce((acc, [slotKey, taskValue]) => {
        const slotMinutes = parseRosterTime(slotKey);
        if (getShiftSlotState(normalizedShift, slotMinutes) === 'work' && taskValue) {
          acc[slotKey] = taskValue;
        }
        return acc;
      }, {});

      return {
        ...current,
        roster: {
          ...current.roster,
          [day]: {
            ...(current.roster[day] || {}),
            [staffId]: normalizedShift,
          },
        },
        tasks: {
          ...current.tasks,
          [day]: {
            ...(current.tasks[day] || {}),
            [staffId]: prunedTasks,
          },
        },
        shiftOptions: normalizedShift
          ? Array.from(new Set([...(current.shiftOptions || []), ...DEFAULT_SHIFT_OPTIONS, normalizedShift]))
          : current.shiftOptions,
      };
    }, normalizedShift ? 'Updating shift...' : 'Clearing shift...');
  };

  const handleShiftChange = (staffId, day, shiftValue) => {
    if (!canManageRoster) return;
    commitShiftValue(staffId, day, shiftValue);
  };

  const handleTaskChange = (staffId, slotKey, value) => {
    if (!canManageRoster) return;

    updateState((current) => {
      const currentTaskMap = { ...(current.tasks[selectedDay]?.[staffId] || {}) };
      if (!value) {
        delete currentTaskMap[slotKey];
      } else {
        currentTaskMap[slotKey] = {
          label: value,
          assignedByRole: String(user?.role || '').trim(),
          assignedByName: String(user?.name || '').trim(),
        };
      }

      return {
        ...current,
        tasks: {
          ...current.tasks,
          [selectedDay]: {
            ...(current.tasks[selectedDay] || {}),
            [staffId]: currentTaskMap,
          },
        },
      };
    }, value ? 'Updating hourly task...' : 'Clearing hourly task...');
  };

  const openCopyTaskModal = (member) => {
    if (!canManageRoster) return;
    const sourceTaskCount = Object.values(tasks?.[selectedDay]?.[member.id] || {})
      .filter((value) => toTaskCell(value).label)
      .length;
    if (sourceTaskCount === 0) {
      toast.error(`Add tasks for ${selectedDay} before copying.`);
      return;
    }

    const workingTargetDays = ROSTER_DAYS.filter((day) => (
      day !== selectedDay && Boolean(parseShift(roster?.[day]?.[member.id] || ''))
    ));
    if (workingTargetDays.length === 0) {
      toast.error('No working target days are available for this staff member.');
      return;
    }

    setCopyTaskModal({
      isOpen: true,
      staffId: member.id,
      staffName: member.name,
      sourceDay: selectedDay,
      targetDays: workingTargetDays,
      overwrite: true,
    });
  };

  const closeCopyTaskModal = () => {
    setCopyTaskModal({
      isOpen: false,
      staffId: '',
      staffName: '',
      sourceDay: ROSTER_DAYS[0],
      targetDays: [],
      overwrite: true,
    });
  };

  const toggleCopyTargetDay = (day) => {
    setCopyTaskModal((current) => {
      const targetDays = current.targetDays.includes(day)
        ? current.targetDays.filter((targetDay) => targetDay !== day)
        : [...current.targetDays, day];
      return { ...current, targetDays };
    });
  };

  const applyCopyTaskModal = () => {
    if (!canManageRoster || !copyTaskModal.staffId) return;
    if (copyTaskModal.targetDays.length === 0) {
      toast.error('Select at least one working day to copy into.');
      return;
    }

    const sourceEntries = Object.entries(tasks?.[copyTaskModal.sourceDay]?.[copyTaskModal.staffId] || {})
      .map(([slotKey, value]) => [slotKey, toTaskCell(value)])
      .filter(([, cell]) => cell.label);

    if (sourceEntries.length === 0) {
      toast.error(`No tasks are available on ${copyTaskModal.sourceDay} to copy.`);
      return;
    }

    const preparedTargets = copyTaskModal.targetDays
      .map((day) => {
        const targetShift = roster?.[day]?.[copyTaskModal.staffId] || '';
        if (!parseShift(targetShift)) return null;

        const copiedTasks = sourceEntries.reduce((acc, [slotKey, cell]) => {
          const slotMinutes = parseRosterTime(slotKey);
          if (getShiftSlotState(targetShift, slotMinutes) === 'work') {
            acc[slotKey] = {
              label: cell.label,
              assignedByRole: String(user?.role || '').trim(),
              assignedByName: String(user?.name || '').trim(),
            };
          }
          return acc;
        }, {});

        return Object.keys(copiedTasks).length ? { day, copiedTasks } : null;
      })
      .filter(Boolean);

    if (preparedTargets.length === 0) {
      toast.error('No matching work-hour slots were found on the selected target days.');
      return;
    }

    updateState((current) => {
      const nextTasks = { ...current.tasks };
      preparedTargets.forEach(({ day, copiedTasks }) => {
        const existingStaffDayTasks = current.tasks?.[day]?.[copyTaskModal.staffId] || {};
        nextTasks[day] = {
          ...(nextTasks[day] || {}),
          [copyTaskModal.staffId]: copyTaskModal.overwrite
            ? copiedTasks
            : { ...copiedTasks, ...existingStaffDayTasks },
        };
      });
      return { ...current, tasks: nextTasks };
    }, `Copying ${copyTaskModal.sourceDay} tasks...`);
    closeCopyTaskModal();
  };

  const addShiftOption = (value) => {
    if (!canManageRoster) return;
    const normalized = normalizeShiftInput(value);
    if (!normalized) {
      toast.error('Invalid shift format. Use HH:MM-HH:MM, Leave, Holiday, or Day Off.');
      return;
    }
    if (normalizedShiftOptions.includes(normalized)) {
      toast('Shift option already exists.');
      setNewShiftOption('');
      return;
    }
    updateState((current) => ({
      ...current,
      shiftOptions: (current.shiftOptions || []).includes(normalized)
        ? current.shiftOptions
        : [...(current.shiftOptions || []), normalized],
    }), 'Adding shift option...');
    setNewShiftOption('');
  };

  const addTaskOption = (value) => {
    if (!canManageRoster) return;
    const nextLabel = String(value || '').trim();
    if (!nextLabel) return;
    if ((taskOptions || []).some((option) => option.toLowerCase() === nextLabel.toLowerCase())) {
      toast('Task option already exists.');
      setNewTaskOption('');
      return;
    }
    updateState((current) => ({
      ...current,
      taskOptions: (current.taskOptions || []).includes(nextLabel)
        ? current.taskOptions
        : [...(current.taskOptions || []), nextLabel],
    }), 'Adding task option...');
    setNewTaskOption('');
  };

  const removeRosterOption = (optionType, value) => {
    if (!canManageRoster) return;
    const safeValue = String(value || '').trim();
    if (!safeValue) return;
    updateState((current) => ({
      ...current,
      shiftOptions: optionType === 'shift'
        ? (current.shiftOptions || []).filter((option) => option !== safeValue)
        : current.shiftOptions,
      taskOptions: optionType === 'task'
        ? (current.taskOptions || []).filter((option) => option !== safeValue)
        : current.taskOptions,
    }), optionType === 'shift' ? 'Removing shift option...' : 'Removing task option...');
  };

  const dayStaffCounts = useMemo(() => {
    const result = visibleStaff.reduce((acc, member) => {
      const shift = roster?.[selectedDay]?.[member.id] || '';
      const parsed = parseShift(shift);
      if (!parsed) return acc;
      if (parsed.durationMinutes >= FULL_TIME_THRESHOLD_MINUTES) {
        acc.fullTime += 1;
      } else {
        acc.partTime += 1;
      }
      return acc;
    }, { fullTime: 0, partTime: 0 });
    return result;
  }, [roster, selectedDay, visibleStaff]);

  const totalWeeklyHours = useMemo(
    () => visibleStaff.reduce((sum, member) => {
      const totalMinutes = calculateWeeklyMinutes(roster, member.id);
      const breakMinutes = ROSTER_DAYS.reduce((breakSum, day) => (
        breakSum + getManualBreakMinutesForMemberDay(tasks, member.id, day)
      ), 0);
      return sum + Math.max(0, totalMinutes - breakMinutes);
    }, 0),
    [roster, tasks, visibleStaff]
  );

  if (!canViewRoster) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Workforce Roster</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="card border-slate-200 bg-gradient-to-b from-white to-slate-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Workforce Roster</h1>
            <p className="mt-1 text-sm text-slate-500">
              {canManageRoster
                ? `Store: ${selectedStore?.name || selectedStore?.storeName || 'Select a store from the header'}`
                : 'View roster schedule and assigned tasks by hour.'}
            </p>
            {canManageRoster && syncState === 'saving' ? (
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving to database...
              </p>
            ) : null}

            {canManageRoster && syncState === 'error' ? (
              <p className="mt-1 text-xs font-medium text-rose-700">
                Failed to sync roster to database.
              </p>
            ) : null}
            {canManageRoster && usersLoadError ? (
              <p className="mt-1 text-xs font-medium text-rose-700">
                Failed to load users list for roster dropdown.
              </p>
            ) : null}
            {canViewRoster && !canLoadRosterScope ? (
              <p className="mt-1 text-xs font-medium text-amber-700">
                Store assignment is missing for this account, so roster data cannot be loaded yet.
              </p>
            ) : null}
            {canManageRoster && canLoadRosterScope && assignableUsers.length === 0 && !loadingUsers && !usersLoadError ? (
              <p className="mt-1 text-xs font-medium text-amber-700">
                No users available.
              </p>
            ) : canManageRoster && canLoadRosterScope && !hasSelectableAssignableUsers && assignableUsers.length > 0 && !loadingUsers && !usersLoadError ? (
              <p className="mt-1 text-xs font-medium text-amber-700">
                All assignable users are already on the roster.
              </p>
            ) : null}
          </div>
          {canManageRoster ? (
            <div className="flex flex-wrap gap-2">
              <FilterSelect
                label="User"
                className="min-w-[260px]"
                value={selectedUserIdToAdd}
                options={addStaffUserOptions}
                onChange={(nextValue) => setSelectedUserIdToAdd(String(nextValue))}
                disabled={loadingUsers || !canLoadRosterScope || assignableUsers.length === 0}
              />
              <button
                type="button"
                className="btn-primary mt-6 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleAddStaff}
                disabled={!selectedUserIdToAdd || !canLoadRosterScope || assignableUsers.length === 0 || rosterMemberIdSet.has(String(selectedUserIdToAdd))}
              >
                <Plus className="mr-1 inline h-4 w-4" />
                Add Member
              </button>
            </div>
          ) : null}
        </div>

        {!isStaffUser ? (
          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)] p-4">
              <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
              <div className="relative flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Team Members</p>
                  <p className="mt-2 text-3xl font-black text-slate-900">{visibleStaff.length}</p>
                </div>
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white/80 shadow-sm">
                  <Users className="h-5 w-5 text-slate-700" />
                </div>
              </div>
            </div>
            <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.18),_transparent_42%),linear-gradient(135deg,_#eff6ff_0%,_#ffffff_48%,_#f0f9ff_100%)] p-4">
              <div className="relative flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">{selectedDay} Part Time</p>
                  <p className="mt-2 text-3xl font-black text-slate-900">{dayStaffCounts.partTime}</p>
                </div>
              </div>
            </div>
            <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.2),_transparent_42%),linear-gradient(135deg,_#ecfdf5_0%,_#ffffff_48%,_#f0fdf4_100%)] p-4">
              <div className="relative flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">{selectedDay} Full Time</p>
                  <p className="mt-2 text-3xl font-black text-slate-900">{dayStaffCounts.fullTime}</p>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      <div className="flex flex-wrap gap-2">
        {[
          { id: 'roster', label: 'Weekly Roster' },
          { id: 'options', label: 'Roster Options' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === tab.id
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'roster' ? (
      <>
      <section className="card overflow-visible p-0">
        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-slate-900">{canManageRoster ? 'Weekly Shift Input' : 'Weekly Shift Schedule'}</h2>
            <ExportButton onClick={exportAllRosterData} size="sm" disabled={!visibleStaff.length} />
          </div>
        </div>
        <div className="overflow-x-auto overflow-y-visible">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-100 px-3 py-3 text-left">Staff</th>
                {ROSTER_DAYS.map((day) => (
                  <th key={day} className="px-3 py-3 text-left">{day}</th>
                ))}
                <th className="px-3 py-3 text-left">Weekly</th>
                {canManageRoster ? <th className="px-3 py-3 text-left">Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {visibleStaff.length === 0 ? (
                <tr>
                  <td colSpan={canManageRoster ? 10 : 9} className="px-4 py-8 text-center text-slate-500">
            {(loadingRoster || isRosterHydrating)
              ? 'Loading roster...'
              : (canManageRoster ? 'No roster users added yet. Click "Add Member" to start.' : 'No roster rows available.')}
                  </td>
                </tr>
              ) : visibleStaff.map((member) => {
                const weeklyMinutes = calculateWeeklyMinutes(roster, member.id);
                const breakMinutes = ROSTER_DAYS.reduce((sum, day) => (
                  sum + getManualBreakMinutesForMemberDay(tasks, member.id, day)
                ), 0);
                const paidMinutes = Math.max(0, weeklyMinutes - breakMinutes);
                const overtime = paidMinutes > OVERTIME_THRESHOLD_MINUTES;
                return (
                  <tr key={member.id} className="border-t border-slate-100">
                    <td className="sticky left-0 z-10 min-w-[190px] bg-white px-3 py-2 text-slate-900 shadow-[inset_-1px_0_0_0_#e2e8f0]">
                      {canManageRoster ? (
                        <input
                          value={member.name}
                          onChange={(event) => handleRenameStaff(member.id, event.target.value)}
                          className="w-full rounded-md border border-slate-300 bg-slate-50 px-2 py-1.5 text-sm font-semibold text-slate-900 outline-none focus:border-primary-400 focus:bg-white"
                        />
                      ) : (
                        <span className="font-semibold">{member.name}</span>
                      )}
                    </td>
                    {ROSTER_DAYS.map((day) => {
                      const shift = roster?.[day]?.[member.id] || '';
                      return (
                        <td key={`${member.id}-${day}`} className="px-3 py-2 align-top">
                          {canManageRoster ? (
                            <FilterSelect
                              className="w-full min-w-[170px]"
                              value={shift}
                              options={shiftSelectOptions}
                              onChange={(nextValue) => handleShiftChange(member.id, day, String(nextValue))}
                              disabled={false}
                            />
                          ) : (
                            <span className="inline-flex min-h-[38px] items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700">
                              {shift || '-'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${overtime ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {formatHourValue(paidMinutes)}
                      </span>
                    </td>
                    {canManageRoster ? (
                      <td className="px-3 py-2">
                        <button type="button" className="text-rose-600 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40" onClick={() => handleRemoveStaff(member.id)} disabled={false}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card overflow-visible p-0">
        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <h2 className="text-lg font-semibold text-slate-900">{canManageRoster ? 'Hourly Task Distribution' : 'Assigned Tasks by Hour'}</h2>
            {!isStaffUser ? (
              <div className="flex flex-wrap gap-2">
                {ROSTER_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDay(day)}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${selectedDay === day ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">Work</span>
            <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">Break</span>
            <span className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">Day Off</span>
            <span className="rounded border border-orange-200 bg-orange-50 px-2 py-1 text-orange-700">Leave</span>
            <span className="rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700">Holiday</span>
          </div>
        </div>
        <div className="overflow-x-auto overflow-y-visible">
          {isStaffUser ? (
            <table className="min-w-[1200px] border-separate border-spacing-0 text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-100 px-3 py-3 text-left">Weekday</th>
                  {HOUR_SLOTS.map((slotMinutes) => (
                    <th key={`staff-${slotMinutes}`} className="px-2 py-3 text-center font-mono text-xs">
                      {buildTaskSlotKey(slotMinutes)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleStaff.length === 0 ? (
                  <tr>
                    <td colSpan={HOUR_SLOTS.length + 1} className="px-4 py-8 text-center text-slate-500">
                      No assigned tasks available.
                    </td>
                  </tr>
                ) : ROSTER_DAYS.map((day) => {
                  const member = visibleStaff[0];
                  return (
                    <tr key={`staff-row-${day}`} className="border-t border-slate-100">
                      <td className="sticky left-0 z-10 min-w-[120px] bg-white px-3 py-2 font-semibold text-slate-900 shadow-[inset_-1px_0_0_0_#e2e8f0]">
                        {day}
                      </td>
                      {HOUR_SLOTS.map((slotMinutes) => {
                        const slotKey = buildTaskSlotKey(slotMinutes);
                        const shift = roster?.[day]?.[member.id] || '';
                        const baseState = getShiftSlotState(shift, slotMinutes);
                        const taskCell = toTaskCell(tasks?.[day]?.[member.id]?.[slotKey]);
                        const slotState = baseState === 'work' && taskCell.label === BREAK_TASK_VALUE ? 'break' : baseState;
                        return (
                          <td key={`staff-cell-${day}-${slotKey}`} className="px-1 py-1">
                            <div className={`rounded border p-1 ${stateClassBySlot[slotState]}`}>
                              {baseState === 'work' ? (
                                <div className="space-y-0.5 py-1 text-center">
                                  <div className="text-[11px] font-semibold">
                                    {taskCell.label === BREAK_TASK_VALUE ? 'Break' : (taskCell.label || '-')}
                                  </div>
                                </div>
                              ) : (
                                <div className="py-1 text-center text-[11px] font-semibold">
                                  {slotState === 'break'
                                    ? 'Break'
                                    : slotState === 'day-off'
                                      ? 'Off'
                                      : slotState === 'leave'
                                        ? 'Leave'
                                        : slotState === 'holiday'
                                          ? 'Holiday'
                                          : '-'}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
          <table className="min-w-[1200px] border-separate border-spacing-0 text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-100 px-3 py-3 text-left">Staff</th>
                {HOUR_SLOTS.map((slotMinutes) => (
                  <th key={slotMinutes} className="px-2 py-3 text-center font-mono text-xs">
                    {buildTaskSlotKey(slotMinutes)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleStaff.length === 0 ? (
                <tr>
                  <td colSpan={HOUR_SLOTS.length + 1} className="px-4 py-8 text-center text-slate-500">
                    {canManageRoster
                    ? 'Add team members and shifts first to distribute hourly tasks.'
                      : 'No assigned tasks available.'}
                  </td>
                </tr>
              ) : visibleStaff.map((member) => (
                <tr key={`${selectedDay}-${member.id}`} className="border-t border-slate-100">
                  <td className="sticky left-0 z-10 min-w-[190px] bg-white px-3 py-2 text-slate-900 shadow-[inset_-1px_0_0_0_#e2e8f0]">
                    <div className="flex min-w-[170px] flex-col gap-1">
                      <span className="font-semibold">{member.name}</span>
                      {canManageRoster ? (
                        <button
                          type="button"
                          className="inline-flex w-fit items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => openCopyTaskModal(member)}
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Copy {selectedDay}
                        </button>
                      ) : null}
                    </div>
                  </td>
                  {HOUR_SLOTS.map((slotMinutes) => {
                    const slotKey = buildTaskSlotKey(slotMinutes);
                    const shift = roster?.[selectedDay]?.[member.id] || '';
                    const baseState = getShiftSlotState(shift, slotMinutes);
                    const taskCell = toTaskCell(tasks?.[selectedDay]?.[member.id]?.[slotKey]);
                    const assignedTask = taskCell.label;
                    const slotState = baseState === 'work' && assignedTask === BREAK_TASK_VALUE ? 'break' : baseState;

                    return (
                      <td key={`${member.id}-${slotKey}`} className="px-1 py-1">
                        <div className={`rounded border p-1 ${stateClassBySlot[slotState]}`}>
                          {baseState === 'work' ? (
                            canManageRoster ? (
                              <FilterSelect
                                className="w-full min-w-[120px]"
                                value={assignedTask}
                                options={taskSelectOptions}
                                onChange={(nextValue) => handleTaskChange(member.id, slotKey, String(nextValue))}
                                menuPlacement="top"
                                disabled={false}
                              />
                            ) : (
                              <div className="space-y-0.5 py-1 text-center">
                                <div className="text-[11px] font-semibold">
                                  {assignedTask === BREAK_TASK_VALUE ? 'Break' : (assignedTask || '-')}
                                </div>
                                {assignedTask && assignedTask !== BREAK_TASK_VALUE && taskCell.assignedByRole ? (
                                  <div className="text-[10px] uppercase tracking-wide opacity-80">
                                    {taskCell.assignedByRole}
                                  </div>
                                ) : null}
                              </div>
                            )
                          ) : (
                            <div className="py-1 text-center text-[11px] font-semibold">
                              {slotState === 'break'
                                ? 'Break'
                                : slotState === 'day-off'
                                  ? 'Off'
                                  : slotState === 'leave'
                                    ? 'Leave'
                                    : slotState === 'holiday'
                                      ? 'Holiday'
                                      : '-'}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </section>

      <div className="text-xs text-slate-500">
        Weekly paid total (all staff): <span className="font-semibold text-slate-700">{formatHourValue(totalWeeklyHours)}</span>
      </div>
      </>
      ) : (
        <section className="card overflow-hidden p-0">
          <div className="border-b border-slate-200 px-4 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Roster Options</h2>
            <p className="mt-1 text-sm text-slate-500">
              Manage the shift and hourly task dropdown values for the selected roster scope.
            </p>
            {!canLoadRosterScope ? (
              <p className="mt-2 text-xs font-medium text-amber-700">
                Select one store from the header to edit options.
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Shift Options</h3>
              </div>
              <div className="space-y-3 p-4">
                {canManageRoster ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      value={newShiftOption}
                      onChange={(event) => setNewShiftOption(event.target.value)}
                      placeholder="07:00-15:00, Day Off, Leave"
                      disabled={!canLoadRosterScope}
                      className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                    />
                    <button
                      type="button"
                      className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => addShiftOption(newShiftOption)}
                      disabled={!newShiftOption.trim() || !canLoadRosterScope}
                    >
                      <Plus className="mr-1 inline h-4 w-4" />
                      Add
                    </button>
                  </div>
                ) : null}
                <div className="divide-y divide-slate-100 rounded-md border border-slate-100">
                  {(shiftOptions.length ? shiftOptions : DEFAULT_SHIFT_OPTIONS).map((option) => (
                    <div key={`shift-${option}`} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">{option}</span>
                      {canManageRoster ? (
                        <button
                          type="button"
                          className="text-rose-600 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                          onClick={() => removeRosterOption('shift', option)}
                          disabled={!canLoadRosterScope}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Task Options</h3>
              </div>
              <div className="space-y-3 p-4">
                {canManageRoster ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      value={newTaskOption}
                      onChange={(event) => setNewTaskOption(event.target.value)}
                      placeholder="Picking, Billing, Cleaning"
                      disabled={!canLoadRosterScope}
                      className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                    />
                    <button
                      type="button"
                      className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => addTaskOption(newTaskOption)}
                      disabled={!newTaskOption.trim() || !canLoadRosterScope}
                    >
                      <Plus className="mr-1 inline h-4 w-4" />
                      Add
                    </button>
                  </div>
                ) : null}
                <div className="divide-y divide-slate-100 rounded-md border border-slate-100">
                  {(taskOptions.length ? taskOptions : DEFAULT_TASK_OPTIONS).map((option) => (
                    <div key={`task-${option}`} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">{option}</span>
                      {canManageRoster ? (
                        <button
                          type="button"
                          className="text-rose-600 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                          onClick={() => removeRosterOption('task', option)}
                          disabled={!canLoadRosterScope}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Modal isOpen={copyTaskModal.isOpen} onClose={closeCopyTaskModal} size="sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Copy {copyTaskModal.sourceDay} Tasks</h3>
              <p className="mt-1 text-sm text-slate-500">
                Copy {copyTaskModal.staffName || 'this staff member'}'s filled hourly tasks into other working days.
              </p>
            </div>
            <button
              type="button"
              onClick={closeCopyTaskModal}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{copySourceTaskCount}</span> filled task slot{copySourceTaskCount === 1 ? '' : 's'} will be copied where the target day has matching work hours.
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-slate-700">Copy to days</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {copyTargetDayOptions.map((option) => {
                const checked = copyTaskModal.targetDays.includes(option.day);
                return (
                  <label
                    key={`copy-target-${option.day}`}
                    className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                      option.isWorkingDay
                        ? 'cursor-pointer border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        : 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-200"
                      checked={checked}
                      disabled={!option.isWorkingDay}
                      onChange={() => toggleCopyTargetDay(option.day)}
                    />
                    <span>
                      <span className="block font-semibold">{option.day}</span>
                      <span className="block text-xs text-slate-500">
                        {option.isWorkingDay ? option.shift : `Skipped: ${option.reason}`}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-200"
              checked={copyTaskModal.overwrite}
              onChange={(event) => setCopyTaskModal((current) => ({ ...current, overwrite: event.target.checked }))}
            />
            <span>
              <span className="block font-semibold">Replace existing target-day tasks</span>
              <span className="block text-xs text-slate-500">
                Turn this off to keep existing cells and only fill empty/matching copied cells.
              </span>
            </span>
          </label>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button type="button" className="btn-secondary" onClick={closeCopyTaskModal}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={applyCopyTaskModal}>
            Copy Tasks
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default WorkforceRoster;
